import React, { useState, useRef, useCallback } from 'react'
import { Table, message } from 'antd'
import styled from 'styled-components'
import { IconGripVertical } from '@tabler/icons-react'

const DragHandle = styled.div`
  cursor: grab;
  color: #999;
  padding: 8px 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: #1890ff;
  }

  &:active {
    cursor: grabbing;
  }
`

const DraggableRow = styled.tr<{ $isDragging?: boolean; $isOver?: boolean }>`
  ${props => props.$isDragging && `
    opacity: 0.5;
    background: #f0f0f0;
  `}

  ${props => props.$isOver && `
    background: #e6f7ff;
    border: 2px dashed #1890ff;
  `}

  &:hover {
    background: #fafafa;
  }
`

interface DraggableTableProps {
  columns: any[]
  dataSource: any[]
  onDragEnd: (dragIndex: number, hoverIndex: number) => void
  rowKey: string
  [key: string]: any
}

const DraggableTable: React.FC<DraggableTableProps> = ({
  columns,
  dataSource,
  onDragEnd,
  rowKey,
  ...props
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const dragRef = useRef<any>(null)

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML)
    e.dataTransfer.setData('text/plain', index.toString())
    setDraggedIndex(index)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedIndex !== null && draggedIndex !== index) {
      setHoveredIndex(index)
    }
  }, [draggedIndex])

  const handleDragLeave = useCallback(() => {
    setHoveredIndex(null)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))

    if (dragIndex !== dropIndex) {
      onDragEnd(dragIndex, dropIndex)
      message.success('순서가 변경되었습니다.')
    }

    setDraggedIndex(null)
    setHoveredIndex(null)
  }, [onDragEnd])

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null)
    setHoveredIndex(null)
  }, [])

  const enhancedColumns = [
    {
      title: '',
      key: 'drag',
      width: 40,
      render: (_: any, __: any, index: number) => (
        <DragHandle
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragEnd={handleDragEnd}
        >
          <IconGripVertical size={16} />
        </DragHandle>
      ),
    },
    ...columns,
  ]

  const customRow = (record: any, index?: number) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => handleDragStart(e, index || 0),
    onDragOver: (e: React.DragEvent) => handleDragOver(e, index || 0),
    onDragLeave: handleDragLeave,
    onDrop: (e: React.DragEvent) => handleDrop(e, index || 0),
    onDragEnd: handleDragEnd,
    style: {
      opacity: draggedIndex === index ? 0.5 : 1,
      background: hoveredIndex === index ? '#e6f7ff' : 'transparent',
      border: hoveredIndex === index ? '2px dashed #1890ff' : 'none',
    },
  })

  return (
    <Table
      {...props}
      columns={enhancedColumns}
      dataSource={dataSource}
      rowKey={rowKey}
      onRow={customRow}
      components={{
        body: {
          row: (props: any) => <DraggableRow {...props} />,
        },
      }}
    />
  )
}

export default DraggableTable